import Checkbox from '@orn/shared-web/components/Checkbox';
import { PhoneNumberInput, usePhoneNumberInput } from '@orn/shared-web/components/Input';
import MessagingCard from '@orn/shared-web/components/MessagingCard';
import { BellIcon, LockIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { useUpdateUser } from '../../../hooks/mutations/useUpdateUser';
import { useCurrentUser } from '../../../hooks/queries/useCurrentUser';
import LegalLinks from '../../LegalLinks';
import { OnboardingTitle } from '../../OnboardingLayout';
import { useOnboardingContext } from '../OnboardingContext';

import OnboardingStepActions from './OnboardingStepActions';

import type { IOnboardingStepProps } from '../types';

export const PHONE_NUMBER_SUBTITLE =
  'Add your phone number for critical updates from Mantle. You can also message the Mantle assistant and text photos of bills for easy upload.';

const HELPER_ITEMS = [
  { icon: <BellIcon />, label: 'Customize notification preferences anytime in settings' },
  { icon: <LockIcon />, label: 'Your phone number is encrypted and never shared' },
];

/**
 * Phone-number entry screen for the new onboarding flow. Captures the user's
 * phone number and persists it via `updateUser`, then advances to
 * {@link PhoneNumberVerifyStep} — which owns the SMS OTP lifecycle (send,
 * resend, verify, skip). The SMS-consent checkbox is required by A2P 10DLC
 * compliance and gates the CTA.
 *
 * The consent checkbox state survives back-nav round-trips via
 * {@link useOnboardingContext}: the E.164 number the user consented for
 * lives outside this component, so a back-arrow → forward round-trip with the
 * same number restores the checked state. Editing the input to a different
 * E.164 clears it — fresh number, fresh consent.
 *
 * Figma: Mantle - UX/UI, node 4145:68299 ("Onboarding Step 04").
 */
const PhoneNumberEntryStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { user } = useCurrentUser();
  const [updateUser] = useUpdateUser();
  const { consentedPhoneE164, setConsentedPhoneE164 } = useOnboardingContext();

  const initialPhoneNumber = user?.phoneNumber ?? '';
  const { value, validation, handleChange } = usePhoneNumberInput(initialPhoneNumber, true);
  const currentE164 = validation.e164Value;
  // Restore checked state when the typed value matches the previously-consented
  // E.164 — covers the back-arrow → forward round-trip case. Drops back to
  // unchecked when the user edits to a different number.
  const smsConsentChecked = consentedPhoneE164 !== null && currentE164 === consentedPhoneE164;
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showConsentError, setShowConsentError] = React.useState(false);
  const consentRef = React.useRef<HTMLDivElement>(null);

  const handleConsentChange = React.useCallback(
    (next: boolean) => {
      setConsentedPhoneE164(next ? currentE164 : null);
      if (next) {
        setShowConsentError(false);
      }
    },
    [currentE164, setConsentedPhoneE164]
  );

  // The CTA stays enabled once the phone number is valid so it never reads as
  // "broken"; the consent gate is enforced on click with an inline error.
  const canSubmit = validation.isValid && Boolean(value) && !isSubmitting;

  const handleContinue = React.useCallback(async () => {
    if (!canSubmit) {
      return;
    }
    if (!smsConsentChecked) {
      setShowConsentError(true);
      consentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      consentRef.current?.querySelector('input')?.focus();
      return;
    }
    setIsSubmitting(true);
    try {
      const e164 = currentE164 || value;
      await updateUser({ variables: { input: { phoneNumber: e164 } } });
      onNext();
    } catch (error) {
      logger.error('Failed to submit phone number', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, currentE164, onNext, smsConsentChecked, updateUser, value]);

  return (
    <>
      <OnboardingTitle title="Your phone number" subtitle={PHONE_NUMBER_SUBTITLE} onBack={onBack} />

      <div className="flex w-full flex-col gap-4">
        <PhoneNumberInput value={value} onChange={handleChange} isRequired validation={validation} />

        <MessagingCard variant="mist">
          <MessagingCard.List items={HELPER_ITEMS} />
        </MessagingCard>

        {/* SMS consent — required for A2P 10DLC compliance. Boxed so the required
            action reads as a distinct gate, and reddens when skipped on submit. */}
        <div
          ref={consentRef}
          className={cx(
            'rounded-xl border px-4 py-3 transition-colors',
            showConsentError ? 'border-error bg-error-primary' : 'border-secondary'
          )}
        >
          <Checkbox
            isSelected={smsConsentChecked}
            onChange={handleConsentChange}
            isInvalid={showConsentError}
            errorMessage="Please accept to continue."
            size="sm"
            label={
              <span className="text-tertiary text-xs">
                By checking this box, you agree to receive transactional and informational SMS messages from Mantle
                about your account. You also agree to Mantle&apos;s <LegalLinks />. Message and data rates may apply.
                Message frequency varies. Reply STOP to opt out, HELP for help.
              </span>
            }
          />
        </div>
      </div>

      <OnboardingStepActions
        isContinueDisabled={!canSubmit}
        isContinueLoading={isSubmitting}
        onContinue={() => void handleContinue()}
      />
    </>
  );
};

PhoneNumberEntryStep.displayName = 'PhoneNumberEntryStep';

export default PhoneNumberEntryStep;
